import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { LANGUAGES } from "../i18n/translations";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { activeProfileId, activeProfile, can } = useProfile();
  const [target, setTarget] = useState({ lower: 70, upper: 180, unit: "mg/dL" });
  const [presc, setPresc] = useState({ fsi: "", ratio_ihc: "", correction_target: "", confirmed: false });
  const [useRec, setUseRec] = useState(false);

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const [tg, pr] = await Promise.all([
      api.get(`/profiles/${activeProfileId}/targets`),
      api.get(`/profiles/${activeProfileId}/prescription`),
    ]);
    if (tg.data.current) setTarget({ lower: tg.data.current.lower, upper: tg.data.current.upper, unit: tg.data.current.unit });
    if (pr.data.current) setPresc({
      fsi: pr.data.current.fsi ?? "", ratio_ihc: pr.data.current.ratio_ihc ?? "",
      correction_target: pr.data.current.correction_target ?? "",
      confirmed: !!pr.data.current.confirmed_by,
    });
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  async function saveTarget() {
    const lo = parseFloat(target.lower), hi = parseFloat(target.upper);
    if (Number.isNaN(lo) || Number.isNaN(hi) || lo >= hi) { toast.error("Limites inválidos."); return; }
    try {
      await api.post(`/profiles/${activeProfileId}/targets`, { lower: lo, upper: hi, unit: target.unit });
      toast.success(t("target_saved"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  async function savePrescription() {
    if (!presc.confirmed) { toast.error(t("prescription_confirm")); return; }
    try {
      await api.post(`/profiles/${activeProfileId}/prescription`, {
        fsi: presc.fsi === "" ? null : parseFloat(presc.fsi),
        ratio_ihc: presc.ratio_ihc === "" ? null : parseFloat(presc.ratio_ihc),
        correction_target: presc.correction_target === "" ? null : parseFloat(presc.correction_target),
        confirmed: presc.confirmed,
      });
      toast.success(t("prescription_saved"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  const canRx = can("manage_prescription");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#0D253F]">{t("settings_title")}</h1>
        <p className="text-[#627D98] mt-1">{t("settings_sub")}</p>
      </div>

      {/* Language */}
      <div className="df-card p-5">
        <h3 className="font-head font-semibold text-[#0D253F] mb-3">{t("language")}</h3>
        <div className="flex gap-2 flex-wrap">
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => setLang(l.code)} data-testid={`set-lang-${l.code}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${lang === l.code ? "bg-[#1A73E8] text-white" : "bg-white border border-[#E3EFF9] text-[#3B526B]"}`}>
              {l.flag} · {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Target */}
        <div className="df-card p-5">
          <h3 className="font-head font-semibold text-[#0D253F]">{t("set_target")}</h3>
          <p className="text-xs text-[#627D98] mt-1 mb-3">{t("set_target_sub")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#627D98]">{t("lower_limit")}</Label>
              <Input inputMode="decimal" value={target.lower} onChange={(e) => setTarget({ ...target, lower: e.target.value })} disabled={!canRx} data-testid="target-lower" />
            </div>
            <div>
              <Label className="text-xs text-[#627D98]">{t("upper_limit")}</Label>
              <Input inputMode="decimal" value={target.upper} onChange={(e) => setTarget({ ...target, upper: e.target.value })} disabled={!canRx} data-testid="target-upper" />
            </div>
          </div>
          <p className="text-xs text-[#627D98] mt-2">Unidade: {target.unit}</p>
          {canRx && <Button onClick={saveTarget} className="mt-3 w-full bg-[#1A73E8] text-white font-semibold" data-testid="save-target">{t("save")}</Button>}
        </div>

        {/* Prescription */}
        <div className="df-card p-5">
          <h3 className="font-head font-semibold text-[#0D253F]">{t("set_prescription")}</h3>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-xs text-[#627D98]">{t("fsi")}</Label>
              <Input inputMode="decimal" value={presc.fsi} onChange={(e) => setPresc({ ...presc, fsi: e.target.value })} disabled={!canRx} data-testid="presc-fsi" />
            </div>
            <div>
              <Label className="text-xs text-[#627D98]">{t("ratio_ihc")}</Label>
              <Input inputMode="decimal" value={presc.ratio_ihc} onChange={(e) => setPresc({ ...presc, ratio_ihc: e.target.value })} disabled={!canRx} data-testid="presc-ratio" />
            </div>
            <div>
              <Label className="text-xs text-[#627D98]">{t("correction_target")}</Label>
              <Input inputMode="decimal" value={presc.correction_target} onChange={(e) => setPresc({ ...presc, correction_target: e.target.value })} disabled={!canRx} data-testid="presc-target" />
            </div>
            {canRx && (
              <>
                <label className="flex items-start gap-2 text-sm text-[#3B526B]">
                  <Checkbox checked={presc.confirmed} onCheckedChange={(v) => setPresc({ ...presc, confirmed: !!v })} data-testid="presc-confirm" />
                  {t("prescription_confirm")}
                </label>
                <Button onClick={savePrescription} className="w-full bg-[#1A73E8] text-white font-semibold" data-testid="save-prescription">{t("save")}</Button>
              </>
            )}
            <p className="text-xs text-[#627D98]">{t("not_clinical_validation")}</p>
          </div>
        </div>
      </div>

      {/* Insulin recommendation */}
      <div className="df-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-head font-semibold text-[#0D253F] flex items-center gap-2"><Lock size={16} /> {t("set_recommendation")}</h3>
        </div>
        <div className="flex items-center justify-between mt-3 py-2">
          <span className="text-sm text-[#3B526B]">{t("use_recommendation")}</span>
          <Switch checked={useRec} onCheckedChange={setUseRec} data-testid="use-recommendation" />
        </div>
        <div className="rounded-xl px-3 py-2.5 text-sm mt-1" style={{ background: "#FEF7E0", border: "1px solid #FDE293", color: "#8a5a00" }} data-testid="engine-status">
          <p className="font-semibold">{t("engine_not_configured")}</p>
          <p className="text-xs mt-1 flex items-start gap-1.5"><Info size={13} className="mt-0.5" /> {t("recommendation_note")}</p>
        </div>
      </div>
    </div>
  );
}
